"use client"

import { LexicalText } from "@/schema"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { $convertFromMarkdownString, $convertToMarkdownString } from "@lexical/markdown"
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isLineBreakNode,
  createEditor,
  type LexicalEditor,
  type LexicalNode,
  type ParagraphNode,
  type SerializedEditorState,
} from "lexical"

import { $createRawHtmlNode } from "@/components/lexical/nodes/raw-html/node"
import { replaceLiquidPlaceholdersInRoot } from "@/components/lexical/transformers/placeholder-html"

import { LEXICAL_NODES, MARKDOWN_TRANSFORMERS } from "../config"

/** ---------- HTML → SerializedEditorState ---------- */
export function htmlToNodes(html: string, opts?: { wrapAsRaw?: boolean }): SerializedEditorState | null {
  if (!html || html.trim() === "") return null

  const editor = createEditor({ nodes: [...LEXICAL_NODES] })

  editor.update(
    () => {
      const root = $getRoot()
      root.clear()

      if (opts?.wrapAsRaw) {
        // Wrap arbitrary HTML in the RawHtmlNode container
        const node = $createRawHtmlNode({ html: html })
        root.append(node)
        return
      }

      // Check if content is plain text (no HTML tags)
      const looksLikePlainText = !/<\w[\s\S]*>/i.test(html)
      if (looksLikePlainText) {
        // Decode entities and create paragraph with text
        const decoded = new DOMParser().parseFromString(html, "text/html").body.textContent ?? ""
        const p = $createParagraphNode()
        if (decoded.length) p.append($createTextNode(decoded))
        root.append(p)
        replaceLiquidPlaceholdersInRoot(root)
        return
      }

      const tempDom = new DOMParser().parseFromString(html, "text/html")
      const rawHtmlDivs = tempDom.body.querySelectorAll('div[data-raw-html="true"]')

      if (rawHtmlDivs.length > 0) {
        const placeholder = "___RAW_HTML_PLACEHOLDER___"
        const rawContents: string[] = []

        rawHtmlDivs.forEach((div) => {
          rawContents.push(div.innerHTML)
          const parent = div.parentElement
          const placeholderNode = tempDom.createTextNode(placeholder)
          if (parent?.tagName === "P" && parent.childNodes.length === 1) {
            parent.replaceWith(placeholderNode)
          } else {
            div.replaceWith(tempDom.createTextNode(placeholder))
          }
        })

        const processedHtml = tempDom.body.innerHTML
        const segments = processedHtml.split(placeholder)

        let rawIndex = 0
        segments.forEach((segment, i) => {
          if (segment.trim()) {
            const segmentDom = new DOMParser().parseFromString(segment, "text/html")
            const segmentNodes = $generateNodesFromDOM(editor, segmentDom)
            segmentNodes.forEach((node) => {
              if ($isElementNode(node) || $isDecoratorNode(node)) {
                root.append(node)
              } else {
                root.append($paragraphForOrphanNode(node))
              }
            })
          }
          if (i < segments.length - 1 && rawIndex < rawContents.length) {
            root.append($createRawHtmlNode({ html: rawContents[rawIndex] }))
            rawIndex++
          }
        })

        if (root.getChildrenSize() === 0) {
          root.append($createParagraphNode())
        }

        replaceLiquidPlaceholdersInRoot(root)
        return
      }

      const dom = new DOMParser().parseFromString(html, "text/html")
      const nodes = $generateNodesFromDOM(editor, dom)

      // ❗️Append directly to root (no selection required)
      if (nodes.length > 0) {
        // Fix orphaned nodes - if we see a pattern of empty paragraph followed by text,
        // the text should be inside the paragraph
        const fixedNodes = []

        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i]
          const nextNode = nodes[i + 1]

          if (
            $isElementNode(node) &&
            node.getChildrenSize() === 0 &&
            nextNode &&
            !$isElementNode(nextNode) &&
            !$isDecoratorNode(nextNode) &&
            !$isLineBreakNode(nextNode)
          ) {
            // Empty paragraph followed by inline content - put the inline content inside
            node.append(nextNode)
            fixedNodes.push(node)
            i++ // Skip the next node since we just consumed it
          } else if ($isElementNode(node) || $isDecoratorNode(node)) {
            // Block-level node, add as-is
            fixedNodes.push(node)
          } else {
            fixedNodes.push($paragraphForOrphanNode(node))
          }
        }

        root.append(...fixedNodes)
      } else {
        root.append($createParagraphNode())
      }

      replaceLiquidPlaceholdersInRoot(root)
    },
    { discrete: true }
  )

  return editor.getEditorState().toJSON()
}

/** ---------- Markdown → SerializedEditorState ---------- */
export function markdownToNodes(markdown: string): SerializedEditorState | null {
  if (!markdown || markdown.trim() === "") return null

  const editor = createEditor({ nodes: [...LEXICAL_NODES] })

  editor.update(
    () => {
      const root = $getRoot()
      root.clear()
      $convertFromMarkdownString(normalizeListIndent(markdown), MARKDOWN_TRANSFORMERS)
      replaceLiquidPlaceholdersInRoot(root)
    },
    { discrete: true }
  )

  return editor.getEditorState().toJSON()
}

const LIST_LINE_REGEX = /^([ \t]*)([-*+]|\d+\.)\s/
const FENCE_REGEX = /^\s*(`{3,}|~{3,})/
const LEXICAL_LIST_INDENT_SIZE = 4

/**
 * Flags each line that sits *inside* a fenced code block (between an opening
 * ``` / ~~~ fence and its matching close). The fence delimiter lines themselves
 * are not flagged. Used so list-indent normalization never rewrites code that
 * merely looks like a list.
 */
function fencedCodeFlags(lines: string[]): boolean[] {
  const flags = new Array<boolean>(lines.length).fill(false)
  let fenceChar: string | null = null
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(FENCE_REGEX)
    if (fenceChar === null) {
      if (match) fenceChar = match[1][0]
    } else if (match && match[1][0] === fenceChar) {
      fenceChar = null
    } else {
      flags[i] = true
    }
  }
  return flags
}

/**
 * @lexical/markdown computes list nesting as `floor(leadingSpaces / 4)`, so
 * 2-space-indented sub-bullets (CommonMark default and what our AI generation
 * emits) collapse to siblings. Rescale list-line leading whitespace so each
 * nesting step is 4 spaces before handing off to `$convertFromMarkdownString`.
 *
 * Exported because the AI summary editor initializes from the raw `markdown`
 * prop via `$convertFromMarkdownString` directly (bypassing `markdownToNodes`),
 * so that path must normalize too — see editors/core.
 */
export function normalizeListIndent(markdown: string): string {
  const lines = markdown.split("\n")
  const isCode = fencedCodeFlags(lines)

  let hasZeroIndentListLine = false
  let smallestUnit = 0
  for (let i = 0; i < lines.length; i++) {
    if (isCode[i]) continue
    const match = lines[i].match(LIST_LINE_REGEX)
    if (!match) continue
    const width = match[1].replace(/\t/g, "    ").length
    if (width === 0) {
      hasZeroIndentListLine = true
      continue
    }
    // Ignore widths of 1: CommonMark allows 1–3 leading spaces before a list marker
    // without implying nesting. A 1-space indent is likely top-level padding, not a
    // sub-bullet, and would pull smallestUnit to 1 causing everything to be over-rescaled.
    if (width < 2) continue
    if (smallestUnit === 0 || width < smallestUnit) smallestUnit = width
  }

  // Only rescale when there are zero-indent anchors confirming indented lines are
  // genuine sub-bullets (not top-level items with minor CommonMark-allowed padding).
  if (!hasZeroIndentListLine || smallestUnit === 0 || smallestUnit >= LEXICAL_LIST_INDENT_SIZE) return markdown

  return lines
    .map((line, i) => {
      if (isCode[i]) return line
      const match = line.match(LIST_LINE_REGEX)
      if (!match) return line
      const leading = match[1]
      if (leading.length === 0) return line
      const width = leading.replace(/\t/g, "    ").length
      const level = Math.floor(width / smallestUnit)
      return " ".repeat(level * LEXICAL_LIST_INDENT_SIZE) + line.slice(leading.length)
    })
    .join("\n")
}

/** ---------- Unified entry point → SerializedEditorState ---------- */
export function toEditorState(
  content: LexicalText | undefined,
  fallback: "html" | "markdown" | "empty" = "html"
): SerializedEditorState {
  if (!content) return emptySerialized()

  if (content.nodes) return content.nodes

  if (content.html) {
    return htmlToNodes(content.html) ?? (fallback === "empty" ? emptySerialized() : emptySerialized())
  }

  if (content.markdown) {
    return markdownToNodes(content.markdown) ?? (fallback === "empty" ? emptySerialized() : emptySerialized())
  }

  return emptySerialized()
}

/** ---------- Generate strings from a *live* editor ---------- */
export function toHTML(editor: LexicalEditor): string {
  const html = $generateHtmlFromNodes(editor)
  return sanitizeGeneratedHtml(html)
}

export function toMarkdown(editor: LexicalEditor): string {
  // Must be called within a read, but helper handles it internally here
  let md = ""
  editor.getEditorState().read(() => {
    md = $convertToMarkdownString(MARKDOWN_TRANSFORMERS)
  })
  return md
}

/** ---------- Helpers ---------- */

/**
 * A <br> between blocks is a blank line in mail clients. Wrapping it in a paragraph
 * as a line break would render as two blank lines in Lexical, so it becomes an empty
 * paragraph instead. Any other orphaned inline node is wrapped as-is.
 */
function $paragraphForOrphanNode(node: LexicalNode): ParagraphNode {
  const paragraph = $createParagraphNode()
  if (!$isLineBreakNode(node)) paragraph.append(node)
  return paragraph
}

function emptySerialized(): SerializedEditorState {
  const editor = createEditor({ nodes: [...LEXICAL_NODES] })
  editor.update(
    () => {
      const root = $getRoot()
      root.clear()
      root.append($createParagraphNode())
    },
    { discrete: true }
  )
  return editor.getEditorState().toJSON()
}

export * from "./merge"

function sanitizeGeneratedHtml(html: string): string {
  if (typeof document === "undefined") return html

  const template = document.createElement("template")
  template.innerHTML = html

  const spans = Array.from(template.content.querySelectorAll("span"))

  spans.forEach((span) => {
    const attributeNames = span.getAttributeNames()
    if (attributeNames.length !== 1 || attributeNames[0] !== "style") {
      return
    }

    const style = span.getAttribute("style")
    if (!style) return

    const normalized = style.replace(/\s/g, "").replace(/;$/, "")
    if (normalized !== "white-space:pre-wrap") {
      return
    }

    const parent = span.parentNode
    if (!parent) return

    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span)
    }

    parent.removeChild(span)
  })

  template.content.querySelectorAll("table").forEach((table) => {
    if (table.closest('[data-raw-html="true"], [data-slant-signature]')) return
    if (table.matches('[role="presentation"], [role="none"]')) return
    if (!table.querySelector("th")) return

    table.setAttribute("data-slant-email-table", "data")
  })

  fixNestedListItems(template.content)
  makeTrailingLineBreaksVisible(template.content)

  return template.innerHTML
}

const PRESERVED_HTML_SELECTOR = '[data-raw-html="true"], [data-slant-signature]'

/**
 * Lexical renders a block whose last child is a line break with an extra <br> so the
 * blank line is visible, but leaves it out of the exported HTML. Mail clients ignore a
 * trailing <br> inside a block, so add it back to match what the editor showed.
 */
function makeTrailingLineBreaksVisible(root: DocumentFragment): void {
  root.querySelectorAll("p, div, li").forEach((block) => {
    if (block.closest(PRESERVED_HTML_SELECTOR)) return

    const children = Array.from(block.childNodes).filter(
      (node) => !(node.nodeType === Node.TEXT_NODE && node.textContent?.trim() === "")
    )
    const last = children[children.length - 1]
    if (children.length < 2 || !(last instanceof HTMLBRElement)) return

    block.appendChild(document.createElement("br"))
  })
}

/**
 * Lexical generates nested lists as separate <li> elements:
 *   <li>Parent text</li><li><ul><li>Child</li></ul></li>
 * This causes double bullets. Merge the nested list into the preceding <li>.
 */
function fixNestedListItems(root: DocumentFragment): void {
  const listItems = Array.from(root.querySelectorAll("li"))

  for (const li of listItems) {
    const meaningfulChildren = Array.from(li.childNodes).filter(
      (node) => !(node.nodeType === Node.TEXT_NODE && node.textContent?.trim() === "")
    )

    if (meaningfulChildren.length !== 1) continue
    const onlyChild = meaningfulChildren[0]
    if (!(onlyChild instanceof HTMLElement) || !["UL", "OL"].includes(onlyChild.tagName)) continue

    const prevLi = li.previousElementSibling
    if (prevLi?.tagName !== "LI") continue

    prevLi.appendChild(onlyChild)
    li.remove()
  }
}
